import axios from 'axios';
import qs from 'qs';
import { ApiClient } from '@/ApiClient';
import { useUser } from '@/store/user';
import { dateToDateOnlyString } from '@lib/formatters';

// The frontend release/build identifier, injected at build time (git SHA on QA,
// release tag on prod) and reused as the Sentry release. Undefined in local dev.
const appVersion = import.meta.env.VITE_RELEASE_TAG;

/**
 * The axios instance every API call goes through. It exists to carry the shared request
 * config (brand header, app version, serialization) — application code should talk to the
 * backend through {@link apiClient} / `useApi()` instead, so requests stay typed against
 * the generated client.
 *
 * Two things reach for it directly, both because they have no generated counterpart:
 * ASP.NET Identity's `/api/account/generated/*` 2FA endpoints, which `MapIdentityApi` maps and
 * which therefore never appear in the OpenAPI document (see `store/user.ts`), and `/api/dev/*`,
 * which is deliberately excluded from the client (see `pages/login/index.vue`).
 */
export const httpClient = axios.create({
  paramsSerializer: {
    serialize: (params) => qs.stringify(params, { allowDots: true }),
  },
  transformRequest: [
    (data, headers) => {
      // Preserve FormData for file uploads
      if (data instanceof FormData) {
        return data;
      }

      // For everything else (primitives, objects, null), use JSON
      if (data !== undefined) {
        headers['Content-Type'] = 'application/json';
        return JSON.stringify(data);
      }

      return data;
    },
  ],
});

httpClient.interceptors.request.use((config) => {
  const url = typeof config.url === 'string' ? config.url : '';
  if (!url.startsWith('/api')) return config;
  const brandId = useUser().brandId;
  if (brandId) config.headers['X-Brand-Id'] = String(brandId);
  // Surfaced as the AppVersion custom dimension on backend request telemetry so we
  // can tell which frontend build a user was running when diagnosing issues.
  if (appVersion) config.headers['X-App-Version'] = String(appVersion);
  // Whether a gate is late is a judgement about the day this user is having, not the one the
  // server is having. Sent on every call so health reads the same in the table, which the server
  // computes, and in the timeline tab, which the browser does. See ClientClock on the backend.
  config.headers['X-Client-Date'] = dateToDateOnlyString(new Date());
  return config;
});

/** Autogenerated api-client, the way to reach the backend from anywhere in the app. */
export const apiClient = new ApiClient(undefined, httpClient);
